import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link2, Loader2, Sparkles, ShieldCheck } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AgentMirror() {
  const { t, lang } = useI18n();
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const simulate = async (e) => {
    e.preventDefault();
    if (!url.trim()) {
      toast.error(lang === "fr" ? "Entrez l'URL de votre entreprise." : "Enter your company URL.");
      return;
    }
    setBusy(true);
    setReport(null);
    try {
      const { data } = await axios.post(`${API}/simulate`, { url: url.trim() });
      setReport(data);
      toast.success(t("success_mirror"));
    } catch {
      toast.error(lang === "fr" ? "Simulation impossible pour le moment." : "Simulation failed for now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="agent-mirror" id="agent-miroir" className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 items-stretch">
        <div className="dncm-hud-frame green rounded-lg p-6 sm:p-8 bg-black/60">
          <div className="flex items-center gap-3 mb-5">
            <span className="dncm-pulse-dot" style={{ background: "var(--dncm-green)", boxShadow: "0 0 12px var(--dncm-green)" }} />
            <span className="font-mono-d text-[11px] uppercase tracking-[0.32em] text-white/65">
              {t("test_mirror_eyebrow")}
            </span>
          </div>
          <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl text-white leading-tight">
            <span className="dncm-green-glow text-[color:var(--dncm-green)]">{t("test_mirror_title").split("—")[0].trim()}</span>
            {t("test_mirror_title").includes("—") ? " — " + t("test_mirror_title").split("—")[1].trim() : ""}
          </h3>
          <p className="mt-2 text-white/65 max-w-xl">{t("test_mirror_caption")}</p>

          <form onSubmit={simulate} className="mt-6 flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Link2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="mirror-url-input"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={lang === "fr" ? "Entrez l'URL de votre entreprise…" : "Enter your company URL…"}
                className="w-full pl-10 pr-4 h-12 rounded-md bg-black/50 border border-white/15 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[color:var(--dncm-green)]/60"
              />
            </div>
            <button
              data-testid="mirror-simulate"
              type="submit"
              disabled={busy}
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md font-mono-d text-[11px] uppercase tracking-[0.22em] dncm-mirror-btn disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {t("simulate_btn")}
            </button>
          </form>

          <div className="mt-3 flex items-center gap-2 text-[11px] text-white/45 font-mono-d">
            <ShieldCheck className="w-3.5 h-3.5 text-[color:var(--dncm-green)]" />
            {lang === "fr" ? "Aucun accès à votre site — lecture publique uniquement." : "No private access — public read only."}
          </div>
        </div>

        <div data-testid="mirror-report" className="dncm-hud-frame rounded-lg p-6 bg-black/60 flex flex-col">
          <div className="font-mono-d text-[10px] uppercase tracking-[0.26em] text-white/55">
            Digital Twin · {lang === "fr" ? "prévisualisation" : "preview"}
          </div>

          {!report && !busy && (
            <div className="flex-1 flex items-center justify-center text-center text-white/40 text-sm py-10">
              {t("no_simulation")}
            </div>
          )}

          {busy && (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 py-10">
              <Loader2 className="w-5 h-5 animate-spin text-[color:var(--dncm-green)]" />
              <div className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/55">{t("simulating")}</div>
            </div>
          )}

          {report && (
            <ul className="mt-3 space-y-2.5">
              {(report.highlights || []).map((h, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-white/80 dncm-fadein" style={{ animationDelay: `${i * 0.06}s` }}>
                  <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full" style={{ background: "var(--dncm-green)", boxShadow: "0 0 8px var(--dncm-green)" }} />
                  <span>{h}</span>
                </li>
              ))}
              {report.recommended_agents && (
                <li className="pt-3 border-t border-white/10 mt-3 font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/55">
                  {lang === "fr" ? "Agents recommandés" : "Recommended agents"} :{" "}
                  <span className="text-[color:var(--dncm-cyan)]">{report.recommended_agents.join(" · ")}</span>
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
